import React, { useState, useEffect } from 'react';
import { useAuthHeader } from 'react-auth-kit';
import axios from 'axios';
import PropTypes from "prop-types";
import { Table, Badge, Button, Modal, Pagination, ButtonGroup, Form, Alert, Spinner } from 'react-bootstrap';
import * as Icon from 'react-feather';
import { mexicoCityToday, parseDateOnlyAsMexicoCityDate } from '../../utils/dateTime';

function ClientPayments(props)  {
    const authHeader = useAuthHeader();

    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [total, setTotal] = useState(0);
    const [totalIva, setTotalIva] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [dateFilter, setDateFilter] = useState('max');
    const [actionError, setActionError] = useState('');
    const [actionSuccess, setActionSuccess] = useState('');
    const [downloadingId, setDownloadingId] = useState(null);
    const [canceling, setCanceling] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [cancelTarget, setCancelTarget] = useState(null);
    const [markingUnpaidId, setMarkingUnpaidId] = useState(null);
    const [showMarkUnpaidModal, setShowMarkUnpaidModal] = useState(false);
    const [markUnpaidTarget, setMarkUnpaidTarget] = useState(null);
    const [cancelReason, setCancelReason] = useState('03');
    const [uuidSustitucion, setUuidSustitucion] = useState('');
    const apiUrl = process.env.REACT_APP_URL;
    const cancelReasons = [
        { value: '01', label: '01 - Comprobante emitido con errores con relacion' },
        { value: '02', label: '02 - Comprobante emitido con errores sin relacion' },
        { value: '03', label: '03 - No se llevo a cabo la operacion' },
        { value: '04', label: '04 - Operacion nominativa relacionada con una factura global' },
    ];

    const get_total = (payments) => {
        let total = 0.0;
        let totalIva = 0.0;
        for (let i = 0; i < payments.length; i++) {
            total+= parseFloat(payments[i].price);
            totalIva+= parseFloat(payments[i].price) + (parseFloat(payments[i].price)) * .16 - (parseFloat(payments[i].price) * .04)
        }
        setTotal(total);
        setTotalIva(totalIva);

    }

    const get_all_trips = () => {
        axios({
            method:'GET',
            url:apiUrl+'/finance/get_client_paid_trips',
            headers: {
                Authorization: authHeader()
            },
        }).then(response => {
            setData(response.data.msg.Payments);
            get_total(response.data.msg.Payments);
        })
        .catch(error => {
            alert(error);
            console.log(error);
        });
    }

    const getComplementStatusBadge = (complement) => {
        const status = complement?.status || 'NOT_GENERATED';
        const statusConfig = {
            STAMPED: { label: 'Timbrado', variant: 'success' },
            CANCELLED: { label: 'Cancelado', variant: 'secondary' },
            ERROR: { label: 'Error', variant: 'danger' },
            DRAFT: { label: 'Borrador', variant: 'warning' },
            NOT_GENERATED: { label: 'No generado', variant: 'light' },
        };
        const config = statusConfig[status] || statusConfig.NOT_GENERATED;
        return <Badge bg={config.variant} text={config.variant === 'light' ? 'dark' : undefined}>{config.label}</Badge>;
    };

    const downloadComplementFile = async (trip, fileType) => {
        const complement = trip.payment_complement;
        if (!complement?.id) {
            return;
        }
        setActionError('');
        setActionSuccess('');
        setDownloadingId(`${complement.id}-${fileType}`);
        try {
            const response = await axios.get(
                `${apiUrl}/cartaporte/payment-complement/download/${complement.id}/${fileType}`,
                { headers: { Authorization: authHeader() } }
            );
            const fileData = response.data.msg;
            const byteCharacters = atob(fileData.file);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const mimeType = fileType === 'pdf' ? 'application/pdf' : 'application/xml';
            const blob = new Blob([byteArray], { type: mimeType });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileData.filename;
            link.click();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            setActionError(error.response?.data?.error || `No se pudo descargar el ${fileType.toUpperCase()}.`);
            console.log(error);
        } finally {
            setDownloadingId(null);
        }
    };

    const openCancelModal = (trip) => {
        setCancelTarget(trip);
        setCancelReason('03');
        setUuidSustitucion('');
        setActionError('');
        setActionSuccess('');
        setShowCancelModal(true);
    };

    const openMarkUnpaidModal = (trip) => {
        setMarkUnpaidTarget(trip);
        setActionError('');
        setActionSuccess('');
        setShowMarkUnpaidModal(true);
    };

    const markTripAsUnpaid = async () => {
        if (!markUnpaidTarget?.id) {
            return;
        }
        setMarkingUnpaidId(markUnpaidTarget.id);
        setActionError('');
        setActionSuccess('');
        try {
            await axios.put(
                `${apiUrl}/finance/client_paid_trips/${markUnpaidTarget.id}/mark_unpaid`,
                {},
                { headers: { Authorization: authHeader() } }
            );
            setActionSuccess('Viaje marcado como no pagado.');
            setShowMarkUnpaidModal(false);
            setMarkUnpaidTarget(null);
            get_all_trips();
        } catch (error) {
            const errData = error.response?.data;
            setActionError(errData?.details || errData?.error || 'No se pudo marcar el viaje como no pagado.');
            console.log(error);
        } finally {
            setMarkingUnpaidId(null);
        }
    };

    const cancelComplement = async () => {
        const complement = cancelTarget?.payment_complement;
        if (!complement?.id) {
            return;
        }
        if (cancelReason === '01' && !uuidSustitucion.trim()) {
            setActionError('Captura el UUID de sustitucion para el motivo 01.');
            return;
        }
        setCanceling(true);
        setActionError('');
        setActionSuccess('');
        try {
            await axios.post(
                `${apiUrl}/cartaporte/payment-complement/cancel/${complement.id}`,
                {
                    motivo: cancelReason,
                    uuid_sustitucion: uuidSustitucion.trim() || null,
                },
                { headers: { Authorization: authHeader() } }
            );
            setActionSuccess('Complemento de pago cancelado exitosamente.');
            setShowCancelModal(false);
            setCancelTarget(null);
            get_all_trips();
        } catch (error) {
            const errData = error.response?.data;
            setActionError(errData?.details || errData?.error || 'No se pudo cancelar el complemento de pago.');
            console.log(error);
        } finally {
            setCanceling(false);
        }
    };

    function addDaysToDate(dateString, daysToAdd) {
        const date = parseDateOnlyAsMexicoCityDate(dateString);
        if (!date) {
            return '';
        }
        date.setDate(date.getDate() + daysToAdd);
        // Format date as YYYY-MM-DD
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
        }

    useEffect(() => {
        get_all_trips();
    }, []);

    // Filter data based on date range (by date_paid)
    useEffect(() => {
        if (data.length === 0) {
            setFilteredData([]);
            return;
        }

        const filterByDateRange = () => {
            const today = parseDateOnlyAsMexicoCityDate(mexicoCityToday());
            today.setHours(0, 0, 0, 0);

            let startDate;

            switch(dateFilter) {
                case '1week':
                    startDate = new Date(today);
                    startDate.setDate(today.getDate() - 7);
                    break;
                case '1month':
                    startDate = new Date(today);
                    startDate.setMonth(today.getMonth() - 1);
                    break;
                case '6months':
                    startDate = new Date(today);
                    startDate.setMonth(today.getMonth() - 6);
                    break;
                case 'ytd':
                    startDate = new Date(today.getFullYear(), 0, 1);
                    break;
                case '1year':
                    startDate = new Date(today);
                    startDate.setFullYear(today.getFullYear() - 1);
                    break;
                case '5years':
                    startDate = new Date(today);
                    startDate.setFullYear(today.getFullYear() - 5);
                    break;
                case 'max':
                default:
                    setFilteredData(data);
                    get_total(data);
                    return;
            }

            const filtered = data.filter(trip => {
                const tripDate = parseDateOnlyAsMexicoCityDate(trip.date_paid);
                return tripDate >= startDate && tripDate <= today;
            });

            setFilteredData(filtered);
            get_total(filtered);
            setCurrentPage(1);
        };

        filterByDateRange();
    }, [data, dateFilter]);

    const handleDateFilterChange = (filter) => {
        setDateFilter(filter);
    };

    // Calculate pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredData.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    // Generate pagination items
    const getPaginationItems = () => {
        const items = [];
        const maxVisible = 5;

        // Always show first page
        if (totalPages > 1) {
            items.push(
                <Pagination.Item
                    key={1}
                    active={1 === currentPage}
                    onClick={() => handlePageChange(1)}
                >
                    1
                </Pagination.Item>
            );
        }

        // Show ellipsis if needed before current range
        if (currentPage > 3) {
            items.push(<Pagination.Ellipsis key="start-ellipsis" />);
        }

        // Show pages around current page
        const startPage = Math.max(2, currentPage - 1);
        const endPage = Math.min(totalPages - 1, currentPage + 1);

        for (let page = startPage; page <= endPage; page++) {
            if (page !== 1 && page !== totalPages) {
                items.push(
                    <Pagination.Item
                        key={page}
                        active={page === currentPage}
                        onClick={() => handlePageChange(page)}
                    >
                        {page}
                    </Pagination.Item>
                );
            }
        }

        // Show ellipsis if needed after current range
        if (currentPage < totalPages - 2) {
            items.push(<Pagination.Ellipsis key="end-ellipsis" />);
        }

        // Always show last page if more than one page
        if (totalPages > 1) {
            items.push(
                <Pagination.Item
                    key={totalPages}
                    active={totalPages === currentPage}
                    onClick={() => handlePageChange(totalPages)}
                >
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

        return (
            <>
            <div className="card mb-4">
                <div className="card-header bg-light">
                    <div className="row">
                        <div className="col-md-6">
                            <h5 className="mb-0">Resumen de Viajes Pagados</h5>
                        </div>
                        <div className="col-md-6 text-end">
                            <div className="d-flex justify-content-end align-items-center">
                                <div className="me-4">
                                    <span className="text-muted">Subtotal: </span>
                                    <strong className="text-dark">${total.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                                </div>
                                <div>
                                    <span className="text-muted">Total: </span>
                                    <strong className="text-success fs-5">${totalIva.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="card-body">
                    {actionError && <Alert variant="danger" onClose={() => setActionError('')} dismissible>{actionError}</Alert>}
                    {actionSuccess && <Alert variant="success" onClose={() => setActionSuccess('')} dismissible>{actionSuccess}</Alert>}
                    {/* Date Range Filter */}
                    <div className="mb-3 d-flex justify-content-end align-items-center">
                        <Form.Label className="me-2 mb-0" style={{whiteSpace: 'nowrap'}}>Filtrar por Fecha:</Form.Label>
                        <ButtonGroup>
                            <Button
                                size="sm"
                                variant={dateFilter === '1week' ? 'primary' : 'outline-primary'}
                                onClick={() => handleDateFilterChange('1week')}
                            >
                                1 Semana
                            </Button>
                            <Button
                                size="sm"
                                variant={dateFilter === '1month' ? 'primary' : 'outline-primary'}
                                onClick={() => handleDateFilterChange('1month')}
                            >
                                1 Mes
                            </Button>
                            <Button
                                size="sm"
                                variant={dateFilter === '6months' ? 'primary' : 'outline-primary'}
                                onClick={() => handleDateFilterChange('6months')}
                            >
                                6 Meses
                            </Button>
                            <Button
                                size="sm"
                                variant={dateFilter === 'ytd' ? 'primary' : 'outline-primary'}
                                onClick={() => handleDateFilterChange('ytd')}
                            >
                                YTD
                            </Button>
                            <Button
                                size="sm"
                                variant={dateFilter === '1year' ? 'primary' : 'outline-primary'}
                                onClick={() => handleDateFilterChange('1year')}
                            >
                                1 Año
                            </Button>
                            <Button
                                size="sm"
                                variant={dateFilter === '5years' ? 'primary' : 'outline-primary'}
                                onClick={() => handleDateFilterChange('5years')}
                            >
                                5 Años
                            </Button>
                            <Button
                                size="sm"
                                variant={dateFilter === 'max' ? 'primary' : 'outline-primary'}
                                onClick={() => handleDateFilterChange('max')}
                            >
                                Todo
                            </Button>
                        </ButtonGroup>
                    </div>

                    {/* <div className="card-header d-flex justify-content-between">
                        <h5 className="card-title">Registros</h5>
                        <AddTrips setData={get_all_trips}/>
                    </div> */}

                    {/* <div className="height-310"> */}
                    <div>
                        <Table className="m-0" responsive>
                            <thead>
                                <tr>
                                    <th>Cliente</th>
                                    <th>Factura</th>
                                    <th>CFDITRASLADO</th>
                                    <th>Días CXP</th>
                                    <th>Subtotal</th>
                                    <th>IVA Trasladado</th>
                                    <th>IVA Retenido</th>
                                    <th>Total</th>
                                    <th>Carta Porte</th>
                                    <th>Fecha entrega de evidencias</th>
                                    <th>Fecha a pagar</th>
                                    <th>Fecha Pagado</th>
                                    <th>Complemento de Pago</th>
                                </tr>
                            </thead>
                            <tbody>
        {currentItems.map(trip => {
          const paymentDueDate = addDaysToDate(trip.formatted_date, trip.days_to_pay);
          const isComplementStamped = trip.payment_complement?.status === 'STAMPED';
          return (
            <tr key={trip.id}>
              <td>{trip.company_name}</td>
              <td>{trip.invoice_id}</td>
              <td>{trip.cfditraslado}</td>
              <td>{trip.days_to_pay}</td>
              <td>${trip.price}</td>
              <td>${(parseFloat(trip.price) * .16)}</td>
              <td>${(parseFloat(trip.price) * .04)}</td>
              <td>${(parseFloat(trip.price) + (parseFloat(trip.price)) * .16 - (parseFloat(trip.price) * .04))}</td>
              <td>Si</td>
              <td>{trip.formatted_date}</td>
              <td>{paymentDueDate}
              </td>
              <td>{trip.date_paid}</td>
              <td>
                <div className="d-flex align-items-center gap-1 flex-wrap">
                    {getComplementStatusBadge(trip.payment_complement)}
                    <Button
                        size="sm"
                        variant="outline-primary"
                        title="Descargar PDF"
                        disabled={trip.payment_complement?.status !== 'STAMPED' || downloadingId !== null}
                        onClick={() => downloadComplementFile(trip, 'pdf')}
                    >
                        {downloadingId === `${trip.payment_complement?.id}-pdf` ? (
                            <Spinner as="span" animation="border" size="sm" role="status" />
                        ) : (
                            <Icon.FileText size={14} />
                        )}
                    </Button>
                    <Button
                        size="sm"
                        variant="outline-secondary"
                        title="Descargar XML"
                        disabled={trip.payment_complement?.status !== 'STAMPED' || downloadingId !== null}
                        onClick={() => downloadComplementFile(trip, 'xml')}
                    >
                        {downloadingId === `${trip.payment_complement?.id}-xml` ? (
                            <Spinner as="span" animation="border" size="sm" role="status" />
                        ) : (
                            <Icon.Code size={14} />
                        )}
                    </Button>
                    <Button
                        size="sm"
                        variant="outline-danger"
                        title={isComplementStamped ? 'Cancelar complemento' : 'Marcar como no pagado'}
                        disabled={canceling || markingUnpaidId !== null}
                        onClick={() => isComplementStamped ? openCancelModal(trip) : openMarkUnpaidModal(trip)}
                    >
                        {markingUnpaidId === trip.id ? (
                            <Spinner as="span" animation="border" size="sm" role="status" />
                        ) : (
                            <Icon.XCircle size={14} />
                        )}
                    </Button>
                </div>
              </td>
            </tr>
          );
        })}
    </tbody>
                        </Table>
                    </div>

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                        <div className="d-flex justify-content-between align-items-center mt-3">
                            <div className="text-muted small">
                                Mostrando {indexOfFirstItem + 1} - {Math.min(indexOfLastItem, filteredData.length)} de {filteredData.length} entradas
                            </div>
                            <Pagination className="mb-0">
                                <Pagination.Prev
                                    disabled={currentPage === 1}
                                    onClick={() => handlePageChange(currentPage - 1)}
                                />
                                {getPaginationItems()}
                                <Pagination.Next
                                    disabled={currentPage === totalPages}
                                    onClick={() => handlePageChange(currentPage + 1)}
                                />
                            </Pagination>
                        </div>
                    )}
                </div>
            </div>

            <Modal show={showCancelModal} onHide={() => !canceling && setShowCancelModal(false)} centered>
                <Modal.Header closeButton={!canceling}>
                    <Modal.Title>Cancelar complemento de pago</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {actionError && <Alert variant="danger">{actionError}</Alert>}
                    <p className="mb-3">
                        Esta seguro que desea cancelar el complemento de pago del viaje #{cancelTarget?.id}?
                    </p>
                    <Form.Group className="mb-3">
                        <Form.Label>Motivo de cancelacion</Form.Label>
                        <Form.Select
                            value={cancelReason}
                            disabled={canceling}
                            onChange={(event) => setCancelReason(event.target.value)}
                        >
                            {cancelReasons.map(reason => (
                                <option key={reason.value} value={reason.value}>{reason.label}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    {cancelReason === '01' && (
                        <Form.Group>
                            <Form.Label>UUID de sustitucion</Form.Label>
                            <Form.Control
                                value={uuidSustitucion}
                                disabled={canceling}
                                onChange={(event) => setUuidSustitucion(event.target.value)}
                                placeholder="UUID del CFDI que sustituye"
                            />
                        </Form.Group>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowCancelModal(false)} disabled={canceling}>
                        Cerrar
                    </Button>
                    <Button
                        variant="danger"
                        onClick={cancelComplement}
                        disabled={canceling || (cancelReason === '01' && !uuidSustitucion.trim())}
                    >
                        {canceling ? (
                            <>
                                <Spinner as="span" animation="border" size="sm" role="status" className="me-1" />
                                Cancelando...
                            </>
                        ) : 'Cancelar complemento'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showMarkUnpaidModal} onHide={() => !markingUnpaidId && setShowMarkUnpaidModal(false)} centered>
                <Modal.Header closeButton={!markingUnpaidId}>
                    <Modal.Title>Marcar viaje como no pagado</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {actionError && <Alert variant="danger">{actionError}</Alert>}
                    <p className="mb-0">
                        Esta seguro que desea marcar el viaje #{markUnpaidTarget?.id} como no pagado?
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowMarkUnpaidModal(false)} disabled={Boolean(markingUnpaidId)}>
                        Cerrar
                    </Button>
                    <Button
                        variant="danger"
                        onClick={markTripAsUnpaid}
                        disabled={Boolean(markingUnpaidId)}
                    >
                        {markingUnpaidId ? (
                            <>
                                <Spinner as="span" animation="border" size="sm" role="status" className="me-1" />
                                Guardando...
                            </>
                        ) : 'Marcar como no pagado'}
                    </Button>
                </Modal.Footer>
            </Modal>
            </>
        );

}

//Status: Entregado, Pendiente, Cancelado, En camino
// Success, Warning, Danger, Info
//https://react-bootstrap.netlify.app/docs/components/badge/
export default ClientPayments;
